import { MentionClass, MentionSourceType, MentionStatus } from '../../interfaces/mention'
import { RELATIONSHIP_TYPE_CATALOG } from '../relationshipTypeCatalog'
import { FilterFieldType, IFilter } from './types'

/**
 * How a row's parties came out of resolution — the axis the review queue is
 * built around, and not a stored column: it is derived from the `*_kind` and
 * `*_resolution_confidence` values across the row's four roles.
 */
export enum MentionResolutionState {
  /** At least one named role is still `unknown`. */
  Unresolved = 'unresolved',
  /** Everything named resolved, but something landed on a weak tier. */
  LowConfidence = 'low_confidence',
  /** Everything named resolved, all of it confidently. */
  Resolved = 'resolved',
}

export const MENTION_RESOLUTION_STATE_LABEL: Record<MentionResolutionState, string> = {
  [MentionResolutionState.Unresolved]: 'Unresolved',
  [MentionResolutionState.LowConfidence]: 'Low confidence',
  [MentionResolutionState.Resolved]: 'Resolved',
}

/**
 * At or below this confidence a match is worth a second look. Set just above the
 * unique-prefix tier (0.65) so it captures that tier and the single-edit fuzzy
 * tier (0.55) — the two that can plausibly link the wrong party — while leaving
 * the first-name tiers (0.7 / 0.75) alone.
 */
export const LOW_CONFIDENCE_MAX = 0.65

const SOURCE_TYPE_LABEL: Record<string, string> = {
  [MentionSourceType.FirefliesTranscript]: 'Call',
  [MentionSourceType.Email]: 'Email',
}

/** The default quick filter, named once so the page can select it on mount. */
export const MENTIONS_NEEDS_ATTENTION = 'Needs my attention'

export const MENTIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'rawName',
    type: FilterFieldType.Text,
    title: 'Name',
    placeholder: 'Name',
    // Custom: one input searching the raw name of all four roles at once, which
    // no single column can express.
    isCustom: true,
  },
  {
    field: 'resolution',
    type: FilterFieldType.List,
    title: 'Resolution',
    placeholder: 'Resolution',
    isMultiple: true,
    isCustom: true,
    options: Object.values(MentionResolutionState).map((value) => ({
      value,
      label: MENTION_RESOLUTION_STATE_LABEL[value],
    })),
  },
  {
    // "Names I am the one who can answer for" — the asserter is the Dwight
    // person who said it, so they are who the question routes to.
    field: 'mine',
    type: FilterFieldType.Boolean,
    title: 'From my calls & emails',
    isCustom: true,
    excludeColumn: true,
    // A Boolean filter renders only when it has options — FilterMenu falls
    // through to no component (and an empty slot) without them.
    options: [
      { value: 'true', label: 'Mine' },
      { value: 'false', label: 'Everyone else' },
    ],
  },
  {
    field: 'sourceType',
    alias: 'mention',
    type: FilterFieldType.List,
    title: 'Source',
    placeholder: 'Source',
    isMultiple: true,
    isSortable: true,
    options: Object.values(MentionSourceType).map((value) => ({
      value,
      label: SOURCE_TYPE_LABEL[value] ?? value,
    })),
  },
  {
    field: 'relationshipType',
    alias: 'mention',
    type: FilterFieldType.List,
    title: 'Relationship',
    placeholder: 'Relationship',
    isMultiple: true,
    isSortable: true,
    options: Object.keys(RELATIONSHIP_TYPE_CATALOG).map((value) => ({
      value,
      label: value.replace(/_/g, ' '),
    })),
  },
  {
    // Filter only, no column: `mention_class` is derived from the relationship
    // type via RELATIONSHIP_TYPE_CATALOG rather than set independently, so as a
    // column it only ever restates the Relationship beside it. It stays a filter
    // because it is the useful axis for excluding incidental name-drops.
    field: 'mentionClass',
    alias: 'mention',
    type: FilterFieldType.List,
    title: 'Class',
    placeholder: 'Class',
    isMultiple: true,
    excludeColumn: true,
    options: Object.values(MentionClass).map((value) => ({ value, label: value })),
  },
  {
    field: 'status',
    alias: 'mention',
    type: FilterFieldType.List,
    title: 'Status',
    placeholder: 'Status',
    isMultiple: true,
    isSortable: true,
    options: Object.values(MentionStatus).map((value) => ({ value, label: value })),
  },
  {
    field: 'occurredAt',
    alias: 'mention',
    type: FilterFieldType.Date,
    title: 'Occurred',
    isSortable: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      // The default. Names from your own calls and emails that resolution could
      // not place — the short list only you can answer.
      {
        title: MENTIONS_NEEDS_ATTENTION,
        filters: {
          // A string, not `true`: the value round-trips through the URL as text,
          // and it has to match the `mine` option value for the control in the
          // filter menu to show the quick filter's own selection.
          mine: 'true',
          resolution: [MentionResolutionState.Unresolved],
          status: [MentionStatus.Active],
        },
      },
      {
        title: 'All unresolved',
        filters: {
          resolution: [MentionResolutionState.Unresolved],
          status: [MentionStatus.Active],
        },
      },
      {
        // Resolved, but on a tier that can link the wrong party — the only way a
        // bad match is ever found.
        title: 'Low confidence',
        filters: {
          resolution: [MentionResolutionState.LowConfidence],
          status: [MentionStatus.Active],
        },
      },
      { title: 'Dismissed', filters: { status: [MentionStatus.Dismissed] } },
    ],
    fields: [
      { field: 'mine', type: FilterFieldType.Boolean },
      { field: 'resolution', type: FilterFieldType.List, isMultiple: true },
      { field: 'status', type: FilterFieldType.List, isMultiple: true },
    ],
  },
] as const satisfies IFilter[]
